/**
 * Configurações (`/app/configuracoes`) — o ÍNDICE dos parâmetros do sistema.
 *
 * Esta tela não edita nada. Ela responde a duas perguntas: "o que dá para
 * configurar?" e "o que já está configurado?". Cada cartão tem título, uma
 * linha explicando e o estado à direita; quem tem tela própria leva até ela,
 * quem ainda não existe abre dizendo o que falta.
 *
 * Por que índice, e não formulário: a versão anterior misturava dado real
 * (empresa) com dez blocos de estado vazio, e a pessoa tinha de rolar a tela
 * inteira para descobrir que quase nada existia. Um índice diz isso de relance.
 *
 * O ESTADO É MEDIDO, nunca inventado. O que vem de `GET /empresa` é real; o que
 * não tem tabela nem rota aparece como "Não configurado" com a explicação do
 * que falta — e não como um formulário bonito que não salva, que é a forma
 * mais rápida de ensinar que o dado foi gravado quando não foi.
 *
 * As grades e tamanhos saíram de "Dados da loja" e foram para "Parâmetros da
 * loja": grade é parâmetro de operação, não identidade da empresa.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Botao } from '../../componentes/Botao'
import { Campo, Chave } from '../../componentes/Campos'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { Selo, type TomSelo } from '../../componentes/Selo'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import {
  carregarConfiguracaoBalcao,
  salvarConfiguracaoBalcao,
  type ConfiguracaoBalcao,
} from '../../plataforma/caixa'
import {
  buscarConfiguracaoCatalogo,
  buscarCores,
  buscarGrade,
  type ConfiguracaoCatalogo,
  type Cores,
  type Grade,
} from '../../plataforma/catalogo'
import { analisar, formatar } from '../../plataforma/dinheiro'
import { buscarEmpresa, type Empresa } from '../../plataforma/empresa'
import { carregarUsuarios, type ListaDeUsuarios } from '../../plataforma/usuarios'
import { LayoutApp } from '../app/LayoutApp'
import { GavetaConfiguracaoCatalogo, GavetaCores, GavetaGrade } from './GavetasCatalogo'

/** Qual gaveta de "Catálogo" está aberta — ou nenhuma. */
type GavetaCatalogo = 'grade' | 'cores' | 'estoque_minimo' | null

type Aba = 'empresa' | 'loja' | 'fiscal' | 'usuarios' | 'pdv' | 'integracoes'

const ABAS: { chave: Aba; rotulo: string }[] = [
  { chave: 'empresa', rotulo: 'Dados da empresa' },
  { chave: 'loja', rotulo: 'Parâmetros da loja' },
  { chave: 'fiscal', rotulo: 'Parâmetros fiscais' },
  { chave: 'usuarios', rotulo: 'Usuários e perfis' },
  { chave: 'pdv', rotulo: 'PDV e TEF' },
  { chave: 'integracoes', rotulo: 'Integrações' },
]

const SUBTITULOS: Record<Aba, string> = {
  empresa: 'Identidade que sai nos documentos',
  loja: 'Grade, tamanhos e regras de operação',
  fiscal: 'Certificado digital e contador',
  usuarios: 'Quem entra no sistema e o que cada perfil pode fazer',
  pdv: 'Pinpad, impressão e regras do balcão',
  integracoes: 'Canais de venda e comunicação conectados à loja',
}

/**
 * O estado de um parâmetro, à direita do cartão.
 *
 * Três tons e nada mais: `ok` é medido e completo, `pendente` é medido e
 * faltando pedaço, `ausente` é "não existe ainda". Um quarto tom viraria
 * decoração.
 */
type Tom = 'ok' | 'pendente' | 'ausente'
type Estado = { rotulo: string; tom: Tom }

type ItemParametro = {
  chave: string
  titulo: string
  descricao: string
  icone: NomeIcone
  estado: Estado
  /** Rota da tela que edita este parâmetro, quando ela existe. */
  destino?: string
  /** O que falta para o parâmetro existir. Só para o que ainda não existe. */
  falta?: string
  /**
   * Abre a gaveta de edição no lugar de navegar ou expandir a explicação do
   * que falta. Existe para o parâmetro que JÁ tem rota e formulário, mas cuja
   * edição não merece página própria — hoje, só "Regras do balcão".
   */
  aoAbrir?: () => void
}

type GrupoParametros = { titulo: string; itens: ItemParametro[] }

const NAO_CONFIGURADO: Estado = { rotulo: 'Não configurado', tom: 'ausente' }

export function TelaConfiguracoes() {
  const [aba, setAba] = useState<Aba>('empresa')
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [balcao, setBalcao] = useState<ConfiguracaoBalcao | null>(null)
  const [gavetaBalcaoAberta, setGavetaBalcaoAberta] = useState(false)
  const [grade, setGrade] = useState<Grade | null>(null)
  const [cores, setCores] = useState<Cores | null>(null)
  const [configCatalogo, setConfigCatalogo] = useState<ConfiguracaoCatalogo | null>(null)
  const [gavetaCatalogoAberta, setGavetaCatalogoAberta] = useState<GavetaCatalogo>(null)
  const [usuarios, setUsuarios] = useState<ListaDeUsuarios | null>(null)
  const [falha, setFalha] = useState('')

  useEffect(() => {
    const cancelamento = new AbortController()

    buscarEmpresa(cancelamento.signal)
      .then(setEmpresa)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setFalha(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar a empresa.')
      })

    return () => cancelamento.abort()
  }, [])

  useEffect(() => {
    const cancelamento = new AbortController()

    // Carrega junto da empresa, e não só ao abrir a aba "Parâmetros da loja":
    // é o mesmo critério de `buscarEmpresa` acima — o estado do cartão (medido,
    // não inventado) precisa existir antes de a pessoa clicar nele.
    carregarConfiguracaoBalcao(cancelamento.signal)
      .then(setBalcao)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        // Falha aqui não derruba a tela inteira — só o cartão de balcão continua
        // "Carregando…"; a empresa e o resto do índice seguem funcionando.
      })

    return () => cancelamento.abort()
  }, [])

  // Os três parâmetros de Catálogo carregam junto com a empresa, pelo mesmo
  // critério do balcão acima: o estado do cartão (medido, não suposto) precisa
  // existir antes de a pessoa poder abrir a gaveta.
  useEffect(() => {
    const cancelamento = new AbortController()
    buscarGrade(cancelamento.signal)
      .then(setGrade)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
      })
    return () => cancelamento.abort()
  }, [])

  useEffect(() => {
    const cancelamento = new AbortController()
    buscarCores(cancelamento.signal)
      .then(setCores)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
      })
    return () => cancelamento.abort()
  }, [])

  useEffect(() => {
    const cancelamento = new AbortController()
    buscarConfiguracaoCatalogo(cancelamento.signal)
      .then(setConfigCatalogo)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
      })
    return () => cancelamento.abort()
  }, [])

  // GET /usuarios exige `users.manage` — só o administrador tem nível total
  // nessa permissão (spec 04). Para quem não é administrador, a chamada
  // devolve 403; o cartão cai para "Ativo" sem número, em vez de mostrar uma
  // falha vermelha por uma tela que a pessoa nem tem como abrir.
  useEffect(() => {
    const cancelamento = new AbortController()
    carregarUsuarios(cancelamento.signal)
      .then(setUsuarios)
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
      })
    return () => cancelamento.abort()
  }, [])

  const grupos = gruposDaAba(
    aba,
    empresa,
    balcao,
    () => setGavetaBalcaoAberta(true),
    grade,
    cores,
    configCatalogo,
    setGavetaCatalogoAberta,
    usuarios,
  )

  return (
    <LayoutApp titulo="Configurações" subtitulo={SUBTITULOS[aba]}>
      {/* Largura toda, como as outras telas: quem dá o recuo lateral é o
          LayoutApp. */}
      <div className="flex w-full flex-col gap-5">
        <div
          role="tablist"
          aria-label="Abas de configurações"
          className="flex flex-wrap gap-5 border-b border-borda"
        >
          {ABAS.map((item) => {
            const ativa = item.chave === aba
            return (
              <button
                key={item.chave}
                type="button"
                role="tab"
                aria-selected={ativa}
                onClick={() => setAba(item.chave)}
                className={`-mb-px border-b-2 pb-3 text-[13.5px] font-semibold whitespace-nowrap ${
                  ativa ? 'border-marca text-marca' : 'border-transparent text-texto-suave'
                }`}
              >
                {item.rotulo}
              </button>
            )
          })}
        </div>

        {falha && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
            {falha}
          </div>
        )}

        {grupos.map((grupo) => (
          <Grupo key={grupo.titulo} titulo={grupo.titulo}>
            {grupo.itens.map((item) => (
              <CartaoDeParametro key={item.chave} item={item} />
            ))}
          </Grupo>
        ))}
      </div>

      {gavetaBalcaoAberta && balcao && (
        <GavetaRegrasDoBalcao
          configuracao={balcao}
          aoFechar={() => setGavetaBalcaoAberta(false)}
          aoSalvar={(nova) => {
            setBalcao(nova)
            setGavetaBalcaoAberta(false)
            mostrarAviso('Regras do balcão salvas.')
          }}
        />
      )}

      {gavetaCatalogoAberta === 'grade' && grade && (
        <GavetaGrade grade={grade} aoFechar={() => setGavetaCatalogoAberta(null)} aoSalvar={setGrade} />
      )}
      {gavetaCatalogoAberta === 'cores' && cores && (
        <GavetaCores cores={cores} aoFechar={() => setGavetaCatalogoAberta(null)} aoSalvar={setCores} />
      )}
      {gavetaCatalogoAberta === 'estoque_minimo' && configCatalogo && (
        <GavetaConfiguracaoCatalogo
          configuracao={configCatalogo}
          aoFechar={() => setGavetaCatalogoAberta(null)}
          aoSalvar={setConfigCatalogo}
        />
      )}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------- o índice */

function gruposDaAba(
  aba: Aba,
  empresa: Empresa | null,
  balcao: ConfiguracaoBalcao | null,
  aoAbrirBalcao: () => void,
  grade: Grade | null,
  cores: Cores | null,
  configCatalogo: ConfiguracaoCatalogo | null,
  aoAbrirCatalogo: (gaveta: GavetaCatalogo) => void,
  usuarios: ListaDeUsuarios | null,
): GrupoParametros[] {
  switch (aba) {
    case 'empresa':
      return grupoEmpresa(empresa)
    case 'loja':
      return grupoLoja(balcao, aoAbrirBalcao, grade, cores, configCatalogo, aoAbrirCatalogo)
    case 'fiscal':
      return grupoFiscal()
    case 'usuarios':
      return grupoUsuarios(usuarios)
    case 'pdv':
      return grupoPDV()
    case 'integracoes':
      return grupoIntegracoes()
  }
}

/**
 * Empresa — o único grupo com estado MEDIDO, porque é o único com rota.
 *
 * "Configurado" aqui quer dizer "dá para emitir documento com isto": razão
 * social, inscrição estadual, CNAE e endereço. Faltando qualquer um, o estado é
 * "Incompleto" e não "Não configurado" — a diferença importa para quem já
 * preencheu metade.
 */
function grupoEmpresa(empresa: Empresa | null): GrupoParametros[] {
  /*
    A logo entra no estado da IDENTIDADE, e não num cartão próprio.

    Ela não conta como "incompleto": dá para emitir documento sem logo, e
    marcar a empresa como pendente por causa dela seria alarme falso. Mas a
    ausência continua dita — "Configurado · sem logo" —, que é a informação
    que o cartão separado dava, sem o segundo caminho para a mesma tela.
  */
  const identidade: Estado = empresa
    ? identidadeCompleta(empresa)
      ? { rotulo: empresa.logo ? 'Configurado' : 'Configurado · sem logo', tom: 'ok' }
      : { rotulo: 'Incompleto', tom: 'pendente' }
    : { rotulo: 'Carregando…', tom: 'ausente' }

  const unidades: Estado = empresa
    ? {
        rotulo: empresa.quantidade_filiais === 1 ? '1 unidade' : `${empresa.quantidade_filiais} unidades`,
        tom: 'ok',
      }
    : { rotulo: 'Carregando…', tom: 'ausente' }

  return [
    {
      titulo: 'Empresa',
      itens: [
        /*
          A logo NÃO tem item próprio aqui.

          Ela tinha, e os dois cartões apontavam para o MESMO destino
          (`/app/empresa`) — dois caminhos, uma tela. Índice que oferece duas
          portas para o mesmo lugar não informa: faz a pessoa escolher entre
          opções que não são opções, e depois descobrir que dava no mesmo.

          A logo é um CAMPO da identidade e vive dentro dela.
        */
        {
          chave: 'identidade',
          titulo: 'Identidade da empresa',
          descricao: 'Razão social, nome fantasia, CNPJ, inscrições, logo, contato e endereço',
          icone: 'store',
          estado: identidade,
          destino: '/app/empresa',
        },
      ],
    },
    {
      titulo: 'Unidades',
      itens: [
        {
          chave: 'unidades',
          titulo: 'Unidades da loja',
          descricao: 'Matriz e filiais, com código e nome',
          icone: 'storefront',
          estado: unidades,
          destino: '/app/empresa',
        },
      ],
    },
  ]
}

function identidadeCompleta(empresa: Empresa): boolean {
  return Boolean(
    empresa.nome &&
      empresa.cnpj &&
      empresa.inscricao_estadual &&
      empresa.cnae &&
      empresa.cep &&
      empresa.cidade &&
      empresa.uf,
  )
}

/**
 * Parâmetros da loja — onde a grade passa a morar.
 *
 * Os três cartões de Catálogo (grade, cores, estoque mínimo) e "Regras do
 * balcão" seguem o MESMO desenho: o estado é MEDIDO (vem da API, mostra
 * "Carregando…" até a resposta chegar) e o clique abre uma `Gaveta`, nunca uma
 * página própria — nenhum dos quatro merece rota dedicada.
 */
function grupoLoja(
  balcao: ConfiguracaoBalcao | null,
  aoAbrirBalcao: () => void,
  grade: Grade | null,
  cores: Cores | null,
  configCatalogo: ConfiguracaoCatalogo | null,
  aoAbrirCatalogo: (gaveta: GavetaCatalogo) => void,
): GrupoParametros[] {
  // Migração 0018: a tabela existe, o backfill garante uma linha por loja, e
  // `GET /caixa/configuracao` sempre devolve algo — "Configurado" é medido, não
  // suposto. Enquanto a chamada não voltou, o cartão mostra "Carregando…" e não
  // abre a gaveta: não há o que editar antes de saber o valor atual.
  const estadoBalcao: Estado = balcao
    ? { rotulo: 'Configurado', tom: 'ok' }
    : { rotulo: 'Carregando…', tom: 'ausente' }

  // A grade sempre existe desde o cadastro inicial (migração 0003 + wizard),
  // mas uma loja de teste ou uma grade esvaziada de propósito (ver
  // catalogo.go, normalizarGrade) pode legitimamente ter zero tamanhos — e
  // isso é "pendente", não "erro".
  const totalTamanhos = grade ? grade.roupas.length + grade.numeracoes.length : 0
  const estadoGrade: Estado = !grade
    ? { rotulo: 'Carregando…', tom: 'ausente' }
    : totalTamanhos > 0
      ? { rotulo: `${grade.roupas.length} roupa · ${grade.numeracoes.length} numeração`, tom: 'ok' }
      : { rotulo: 'Sem tamanho definido', tom: 'pendente' }

  // Paleta vazia não é "faltando": ela é opcional desde sempre (a cor sempre
  // foi texto livre por produto), então zero cores salvas continua "ok".
  const estadoCores: Estado = !cores
    ? { rotulo: 'Carregando…', tom: 'ausente' }
    : {
        rotulo: cores.cores.length === 0 ? 'Nenhuma cor salva' : `${cores.cores.length} ${cores.cores.length === 1 ? 'cor salva' : 'cores salvas'}`,
        tom: 'ok',
      }

  const estadoConfigCatalogo: Estado = !configCatalogo
    ? { rotulo: 'Carregando…', tom: 'ausente' }
    : { rotulo: `${configCatalogo.estoque_minimo_padrao} peças`, tom: 'ok' }

  return [
    {
      titulo: 'Catálogo',
      itens: [
        {
          chave: 'grade',
          titulo: 'Grade de tamanhos',
          descricao: 'Tamanhos de roupa e numeração usados no cadastro de produto',
          icone: 'grid_view',
          estado: estadoGrade,
          aoAbrir: grade ? () => aoAbrirCatalogo('grade') : undefined,
        },
        {
          chave: 'cores',
          titulo: 'Cores do catálogo',
          descricao: 'A paleta oferecida ao cadastrar uma variação',
          icone: 'apparel',
          estado: estadoCores,
          aoAbrir: cores ? () => aoAbrirCatalogo('cores') : undefined,
        },
      ],
    },
    {
      titulo: 'Operação',
      itens: [
        {
          chave: 'estoque_minimo',
          titulo: 'Estoque mínimo padrão',
          descricao: 'O ponto de reposição aplicado a SKU novo',
          icone: 'atraso',
          estado: estadoConfigCatalogo,
          aoAbrir: configCatalogo ? () => aoAbrirCatalogo('estoque_minimo') : undefined,
        },
        {
          chave: 'balcao',
          titulo: 'Regras do balcão',
          descricao: 'Desconto máximo do vendedor, limite da gaveta e prazo de troca',
          icone: 'lock',
          estado: estadoBalcao,
          aoAbrir: balcao ? aoAbrirBalcao : undefined,
        },
      ],
    },
  ]
}

/**
 * Parâmetros fiscais — nenhuma linha de backend por trás, e a tela diz isso.
 *
 * É aqui que o certificado digital e o contador vão morar. Não há tabela,
 * rota nem upload: os cartões abrem explicando o que falta, e NÃO existe
 * formulário — campo que não salva ensina que o dado foi salvo.
 */
function grupoFiscal(): GrupoParametros[] {
  return [
    {
      titulo: 'Emissão',
      itens: [
        {
          chave: 'certificado',
          titulo: 'Certificado digital',
          descricao: 'O A1 que assina a NF-e e a NFC-e',
          icone: 'shield',
          estado: NAO_CONFIGURADO,
          falta:
            'Guardar um certificado A1 exige o arquivo .pfx e a senha dele, cifrados em repouso, com data de validade vigiada e alerta antes de vencer. Nada disso existe no banco hoje, e um upload sem cifragem seria pior que não ter a função.',
        },
        {
          chave: 'serie',
          titulo: 'Série e numeração',
          descricao: 'Série da NFC-e, próximo número e contingência offline',
          icone: 'receipt_long',
          estado: NAO_CONFIGURADO,
          falta:
            'Não existe tabela de série nem controle de numeração fiscal na API. Emitir nota depende do módulo fiscal, que ainda não foi escrito.',
        },
        {
          chave: 'csc',
          titulo: 'CSC e ambiente da SEFAZ',
          descricao: 'Código de segurança do contribuinte e homologação/produção',
          icone: 'lock',
          estado: NAO_CONFIGURADO,
          falta:
            'O CSC é credencial e seguiria o mesmo cuidado do certificado. Sem o módulo fiscal, não há para onde enviar a nota nem o que autenticar.',
        },
      ],
    },
    {
      titulo: 'Tributação',
      itens: [
        {
          chave: 'regime',
          titulo: 'Regime tributário',
          descricao: 'Simples Nacional, Lucro Presumido ou Lucro Real',
          icone: 'percent',
          estado: NAO_CONFIGURADO,
          falta:
            'O regime muda o cálculo de imposto de toda venda, e o cálculo não existe. Gravar o campo antes disso seria guardar um dado que nada lê.',
        },
        {
          chave: 'padroes',
          titulo: 'Padrões fiscais por categoria',
          descricao: 'CFOP, CSOSN, origem e NCM herdados no cadastro de produto',
          icone: 'edit_note',
          estado: NAO_CONFIGURADO,
          falta:
            'O produto já tem NCM próprio (migração 0003), mas não existe herança por categoria nem tabela de padrão da loja.',
        },
      ],
    },
    {
      titulo: 'Contador',
      itens: [
        {
          chave: 'contador',
          titulo: 'Escritório contábil',
          descricao: 'Quem recebe o pacote mensal de XMLs',
          icone: 'person',
          estado: NAO_CONFIGURADO,
          falta:
            'Cadastrar o contador só faz sentido junto do envio automático dos XMLs, e não há XML para enviar enquanto o módulo fiscal não emitir nota.',
        },
      ],
    },
  ]
}

/**
 * Acesso — as três telas que nasceram nesta feature.
 *
 * "Usuários" e "Perfis e permissões" agora têm rota de verdade, com estado
 * MEDIDO: o total de usuários vem de `GET /usuarios` (503/403 — quem não é
 * administrador não vê o número, e o cartão cai para "Ativo" sem inventar
 * uma contagem). "PIN de autorização" NÃO virou tela própria — ela aponta
 * para "Meu perfil", onde cada um já define o próprio, no mesmo padrão da
 * troca de senha.
 */
function grupoUsuarios(usuarios: ListaDeUsuarios | null): GrupoParametros[] {
  const estadoUsuarios: Estado = usuarios
    ? { rotulo: `${usuarios.total} ${usuarios.total === 1 ? 'usuário' : 'usuários'}`, tom: 'ok' }
    : { rotulo: 'Ativo', tom: 'ok' }

  return [
    {
      titulo: 'Acesso',
      itens: [
        {
          chave: 'usuarios',
          titulo: 'Usuários',
          descricao: 'Convidar, listar e trocar papel ou situação de quem acessa a loja',
          icone: 'group',
          estado: estadoUsuarios,
          destino: '/app/usuarios',
        },
        {
          chave: 'perfis',
          titulo: 'Papéis e permissões',
          descricao: 'O que cada função pode fazer — os seis papéis de fábrica e os personalizados da loja',
          icone: 'shield',
          estado: { rotulo: 'Ativo', tom: 'ok' },
          destino: '/app/usuarios?aba=perfis',
        },
        {
          chave: 'pin',
          titulo: 'PIN de autorização',
          descricao: 'Os quatro dígitos que liberam desconto e ajuste no balcão',
          icone: 'lock',
          estado: { rotulo: 'Em Meu perfil', tom: 'ok' },
          destino: '/app/perfil',
        },
      ],
    },
  ]
}

function grupoPDV(): GrupoParametros[] {
  return [
    {
      titulo: 'Balcão',
      itens: [
        {
          chave: 'pinpad',
          titulo: 'Pinpad e TEF',
          descricao: 'Integração com a adquirente no balcão',
          icone: 'point_of_sale',
          estado: NAO_CONFIGURADO,
          falta:
            'Nenhuma integração com adquirente (Stone, Cielo, Rede) existe na API. Hoje a forma de pagamento é registrada na venda, sem falar com maquininha.',
        },
        {
          chave: 'impressao',
          titulo: 'Impressão e cupom',
          descricao: 'O que sai para o cliente depois da venda',
          icone: 'receipt_long',
          estado: NAO_CONFIGURADO,
          falta:
            'Não há configuração de impressora nem mensagem de rodapé gravada na API.',
        },
      ],
    },
  ]
}

function grupoIntegracoes(): GrupoParametros[] {
  const itens: { chave: string; titulo: string; descricao: string; icone: NomeIcone }[] = [
    { chave: 'loja_virtual', titulo: 'Loja virtual', descricao: 'Catálogo e pedidos do e-commerce', icone: 'storefront' },
    { chave: 'marketplace', titulo: 'Marketplace', descricao: 'Anúncio e estoque em canal externo', icone: 'sell' },
    { chave: 'whatsapp', titulo: 'WhatsApp', descricao: 'Cupom e cobrança de crediário', icone: 'sync' },
    { chave: 'pix', titulo: 'Gateway PIX', descricao: 'Baixa automática de pagamento', icone: 'payments' },
  ]

  return [
    {
      titulo: 'Canais',
      itens: itens.map((item) => ({
        ...item,
        estado: NAO_CONFIGURADO,
        falta:
          'Nenhuma integração de canal tem tabela, credencial ou rota na API hoje. Nenhum status de conexão apareceria aqui de verdade.',
      })),
    },
  ]
}

/* --------------------------------------------------------------- peças */

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 px-1 text-[10.5px] font-bold tracking-[0.6px] text-texto-suave uppercase">
        {titulo}
      </h2>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  )
}

/**
 * O cartão do índice.
 *
 * Quem tem tela vira `Link`; quem ainda não existe vira `button` que ABRE a
 * explicação do que falta. São elementos diferentes de propósito: um navega e
 * o outro não, e usar `div` clicável nos dois casos tiraria os dois do teclado.
 */
function CartaoDeParametro({ item }: { item: ItemParametro }) {
  const [aberto, setAberto] = useState(false)

  const miolo = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sistema bg-marca-suave text-marca">
        <Icone nome={item.icone} tamanho={18} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-tinta">{item.titulo}</span>
        <span className="block truncate text-[12.5px] text-texto-suave">{item.descricao}</span>
      </span>

      <SeloDeEstado estado={item.estado} />

      {/* Seta para quem NAVEGA ou ABRE GAVETA, mais/menos para quem só abre uma
          explicação: a mesma seta nos três casos prometeria uma tela que não
          existe. */}
      <Icone
        nome={item.destino || item.aoAbrir ? 'chevron_direita' : aberto ? 'close' : 'add'}
        tamanho={16}
        className="shrink-0 text-inativo"
      />
    </>
  )

  const base =
    'flex w-full items-center gap-3.5 rounded-sistema border border-borda bg-superficie px-4 py-3 text-left hover:border-marca-borda hover:bg-marca-fundo'

  if (item.destino) {
    return (
      <Link to={item.destino} className={base}>
        {miolo}
      </Link>
    )
  }

  // Tem edição (uma gaveta), mas não é rota: abre a gaveta em vez de expandir
  // a explicação do que falta — não HÁ o que falta, o parâmetro já existe.
  if (item.aoAbrir) {
    return (
      <button type="button" onClick={item.aoAbrir} className={base}>
        {miolo}
      </button>
    )
  }

  return (
    <div>
      <button type="button" aria-expanded={aberto} onClick={() => setAberto((estava) => !estava)} className={base}>
        {miolo}
      </button>

      {aberto && item.falta && (
        <div className="mt-1.5 flex gap-3 rounded-sistema border border-marca-borda bg-marca-fundo-2 px-4 py-3.5">
          <Icone nome="info" tamanho={18} className="shrink-0 text-marca" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-marca-forte">O que falta para isto existir</p>
            <p className="mt-1 text-[12.5px] leading-[1.55] text-marca-forte">{item.falta}</p>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * O estado à direita do cartão, no chip do design system.
 *
 * O tom do índice traduz para o do `Selo`: `pendente` é âmbar (espera alguém),
 * `ausente` é neutro (não existe ainda, e não é urgência). Nada aqui é
 * vermelho — "não configurado" não travou nada.
 */
function SeloDeEstado({ estado }: { estado: Estado }) {
  const tons: Record<Tom, TomSelo> = { ok: 'ok', pendente: 'atencao', ausente: 'neutro' }

  return (
    <span className="hidden shrink-0 sm:block">
      <Selo tom={tons[estado.tom]}>{estado.rotulo}</Selo>
    </span>
  )
}

/**
 * A gaveta que edita "Regras do balcão" (migração 0018).
 *
 * Percentual entra como número inteiro de 0 a 100 — a pessoa digita "10", não
 * "1000" — e vira ponto-base só no envio (`× 100`), porque ponto-base é a
 * unidade da API e do banco, não a que se digita num balcão (CONSTITUICAO.md
 * §1). O limite da gaveta usa o mesmo campo de dinheiro em texto livre do
 * resto do sistema (`plataforma/dinheiro.analisar`), como o diálogo de
 * abertura de turno.
 */
function GavetaRegrasDoBalcao({
  configuracao,
  aoFechar,
  aoSalvar,
}: {
  configuracao: ConfiguracaoBalcao
  aoFechar: () => void
  aoSalvar: (nova: ConfiguracaoBalcao) => void
}) {
  const [descontoPercentual, setDescontoPercentual] = useState(
    String(configuracao.desconto_maximo_pontos_base / 100),
  )
  const [limiteGaveta, setLimiteGaveta] = useState(formatar(configuracao.limite_gaveta_centavos))
  const [prazoTroca, setPrazoTroca] = useState(String(configuracao.prazo_troca_dias))
  const [bloquear, setBloquear] = useState(configuracao.bloquear_venda_sem_turno)
  const [erros, setErros] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (salvando) return

    const desconto = Number(descontoPercentual.trim().replace(',', '.'))
    const limite = limiteGaveta.trim() === '' ? 0 : analisar(limiteGaveta)
    const prazo = Number(prazoTroca.trim())

    const camposInvalidos: Record<string, string> = {}
    if (!Number.isFinite(desconto) || desconto < 0 || desconto > 100) {
      camposInvalidos.desconto_maximo_pontos_base = 'Informe um percentual entre 0 e 100.'
    }
    if (limite === null || limite < 0) {
      camposInvalidos.limite_gaveta_centavos = 'Confira o valor — não pode ser negativo.'
    }
    if (!Number.isInteger(prazo) || prazo < 0 || prazo > 365) {
      camposInvalidos.prazo_troca_dias = 'Informe um prazo entre 0 e 365 dias.'
    }
    if (Object.keys(camposInvalidos).length > 0) {
      setErros(camposInvalidos)
      return
    }
    setErros({})

    setSalvando(true)
    try {
      const salva = await salvarConfiguracaoBalcao({
        desconto_maximo_pontos_base: Math.round(desconto * 100),
        limite_gaveta_centavos: limite ?? 0,
        prazo_troca_dias: prazo,
        bloquear_venda_sem_turno: bloquear,
      })
      aoSalvar(salva)
    } catch (causa: unknown) {
      if (causa instanceof ErroApi) {
        setErros(causa.campos)
        mostrarAviso(causa.message, 'erro')
      } else {
        mostrarAviso('Não foi possível salvar as regras do balcão.', 'erro')
      }
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo="Regras do balcão" largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo="Regras do balcão"
        subtitulo="Desconto máximo, limite da gaveta e prazo de troca"
        aoFechar={aoFechar}
      />

      <CorpoDaGaveta>
        <form
          onSubmit={(evento) => {
            evento.preventDefault()
            void salvar()
          }}
          className="flex flex-col gap-5"
        >
          <Campo
            rotulo="Desconto máximo sem PIN"
            inputMode="decimal"
            value={descontoPercentual}
            onChange={(evento) => setDescontoPercentual(evento.target.value)}
            placeholder="10"
            erro={erros.desconto_maximo_pontos_base}
            dica="Percentual sobre o preço de tabela. Acima disso, o vendedor precisa do PIN de alguém com a permissão (spec 04)."
          />

          <Campo
            rotulo="Limite da gaveta"
            inputMode="decimal"
            value={limiteGaveta}
            onChange={(evento) => setLimiteGaveta(evento.target.value)}
            placeholder="0,00"
            erro={erros.limite_gaveta_centavos}
            dica="Valor em dinheiro na gaveta que sugere sangria. Zero desliga o alerta."
          />

          <Campo
            rotulo="Prazo de troca (dias)"
            inputMode="numeric"
            value={prazoTroca}
            onChange={(evento) => setPrazoTroca(evento.target.value.replace(/\D/g, '').slice(0, 3))}
            placeholder="30"
            erro={erros.prazo_troca_dias}
            dica="De 0 a 365 dias. É o prazo padrão da troca com cupom e etiqueta (spec 05)."
          />

          <div className="border-t border-divisoria pt-5">
            <Chave
              ligada={bloquear}
              aoMudar={setBloquear}
              rotulo="Bloquear venda sem turno aberto"
              ajuda="Ligado, o vendedor não consegue mais registrar uma venda se não houver turno de caixa aberto na filial — é preciso abrir o caixa antes de vender. Desligado (o padrão de hoje), a venda continua sendo aceita mesmo sem turno, como sempre foi."
            />
          </div>
        </form>
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Botao>
        <Botao icone="check" disabled={salvando} onClick={() => void salvar()}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}
